// Astarte-on-glitch: restore the inline styles produced by BBCode
// ([colorhex], [rotate], [rotatez]) when status HTML is turned into React
// elements. `htmlStringToComponents` only keeps allow-listed attributes and
// has no notion of `style`, so the styles the server emits would be dropped.
//
// The server-side sanitizer (Sanitize::Config::MASTODON_OUTGOING) only lets
// `color`, `transform` and `display` through; the same allow-list is applied
// here so remote content can never inject anything else.

import type { CSSProperties } from 'react';

const ALLOWED_STYLE_PROPERTIES: ReadonlySet<string> = new Set([
  'color',
  'transform',
  'display',
]);

// Enough for `#a55a4a`, `rotateZ(45deg)`, `rotate3d(1, -1, 0, 180deg)` and
// `inline-block`; excludes anything that could smuggle in url(), quotes or
// escapes.
const SAFE_VALUE_RE = /^[\w\s#().,%+-]+$/;

export function parseAstarteStyle(style: string): CSSProperties | null {
  const result: CSSProperties = {};

  for (const declaration of style.split(';')) {
    const separator = declaration.indexOf(':');
    if (separator < 0) {
      continue;
    }

    const property = declaration.slice(0, separator).trim().toLowerCase();
    const value = declaration.slice(separator + 1).trim();

    if (!ALLOWED_STYLE_PROPERTIES.has(property) || !SAFE_VALUE_RE.test(value)) {
      continue;
    }

    result[property as 'color' | 'transform' | 'display'] = value;
  }

  return Object.keys(result).length > 0 ? result : null;
}

// `onAttribute` handler for EmojiHTML / htmlStringToComponents. Returning
// `undefined` leaves every other attribute to the default allow-list.
export function astarteStatusAttribute(
  name: string,
  value: string,
  tagName: string,
): [string, unknown] | null | undefined {
  if (name !== 'style') {
    return undefined;
  }

  if (tagName.toLowerCase() !== 'span') {
    return null;
  }

  const style = parseAstarteStyle(value);

  return style ? ['style', style] : null;
}
